import type {LegalDocumentResponse} from '@getmoss/api-gateway'
import type {Content} from '@prismicio/client'
import type {SliceComponentProps} from '@prismicio/react'
import {PrismicRichText} from '@prismicio/react'
import {useRouter} from 'next/router'
import type {FC} from 'react'
import {useMemo, useState} from 'react'
import type {LocalesType} from 'src/common/constants'
import {Container} from 'src/common/Container'
import {useMossApi} from 'src/common/hooks/useMossApi'
import useSWR from 'swr'
// eslint-disable-next-line boundaries/dependencies -- grandfathered cross-slice import, predates the slice-boundaries rule
import {CustomSelect} from '../IntegrationsContainer/CustomSelect'
import {DocumentRow} from './components/DocumentRow'
import {TRANSLATIONS} from './constants'
import {
  buildSearchRequest,
  getDocumentTypeFilterOptions,
  getMarketFilterOptions,
  getProductFilterOptions,
} from './utils'

export type LegalDocumentsProps = SliceComponentProps<Content.LegalDocumentsSlice>

const LegalDocuments: FC<LegalDocumentsProps> = ({slice}) => {
  const {locale = 'en-us'} = useRouter()
  const currentLocale = locale as LocalesType

  const [productIndices, setProductIndices] = useState<number[]>([])
  const [marketIndex, setMarketIndex] = useState<number>(0)
  const [documentTypeIndex, setDocumentTypeIndex] = useState<number>(0)

  const productFilterOptions = useMemo(() => getProductFilterOptions(currentLocale), [currentLocale])
  const marketFilterOptions = useMemo(() => getMarketFilterOptions(currentLocale), [currentLocale])
  const documentTypeFilterOptions = useMemo(() => getDocumentTypeFilterOptions(currentLocale), [currentLocale])

  const selectedProductValues = useMemo(
    () => productIndices.map((index) => productFilterOptions[index].value).filter((v) => v !== undefined),
    [productIndices, productFilterOptions],
  )

  const selectedMarketValue = marketFilterOptions[marketIndex].value
  const selectedDocumentTypeValue = documentTypeFilterOptions[documentTypeIndex].value

  const searchRequest = useMemo(
    () => buildSearchRequest(selectedProductValues, selectedMarketValue, selectedDocumentTypeValue),
    [selectedProductValues, selectedMarketValue, selectedDocumentTypeValue],
  )

  const {mossApi} = useMossApi({})

  const swrKey = JSON.stringify(searchRequest)

  const {data, isLoading, error} = useSWR<LegalDocumentResponse[]>(
    swrKey,
    async () => (await mossApi.apiLegalDocumentController.searchActiveLegalDocuments(searchRequest)).data,
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    },
  )

  const hasDocuments = !isLoading && data && data.length > 0
  const hasNoDocuments = !isLoading && data && data.length === 0

  return (
    <Container
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      backgroundColor="Moss White"
      className="!py-12 md:!py-16"
    >
      <div className="mx-auto w-full max-w-[1200px] px-5 md:px-10 lg:px-5 xl:px-0">
        <div className="mb-8 flex flex-col gap-4">
          {slice.primary.title && (
            <h1 className="text-32 font-medium leading-[40px] text-moss-black md:text-40 md:leading-[48px]">
              {slice.primary.title}
            </h1>
          )}
          <PrismicRichText
            components={{
              paragraph: ({children}) => <p className="text-14 leading-[20px] text-moss-grey-500">{children}</p>,
              list: ({children}) => (
                <ul className="list-disc pl-5 text-14 leading-[20px] text-moss-grey-500">{children}</ul>
              ),
              listItem: ({children}) => <li className="mb-1">{children}</li>,
              strong: ({children}) => <strong className="font-medium">{children}</strong>,
            }}
            field={slice.primary.subtitlev2}
          />
        </div>

        <div className="mb-8 flex flex-col gap-4 sm:flex-row sm:gap-4">
          <div className="w-full sm:w-[200px]">
            <CustomSelect
              v2
              options={productFilterOptions}
              multiSelect
              activeIndices={productIndices}
              setActiveIndices={setProductIndices}
              activeIndex={0}
              setActiveIndex={() => {}}
              trackingData={{mktgId: 'legal-documents-product-select', dataType: 'legal-product'}}
            />
          </div>
          <div className="w-full sm:w-[200px]">
            <CustomSelect
              v2
              options={marketFilterOptions}
              activeIndex={marketIndex}
              setActiveIndex={setMarketIndex}
              trackingData={{mktgId: 'legal-documents-market-select', dataType: 'legal-market'}}
            />
          </div>
          <div className="w-full sm:w-[200px]">
            <CustomSelect
              v2
              options={documentTypeFilterOptions}
              activeIndex={documentTypeIndex}
              setActiveIndex={setDocumentTypeIndex}
              trackingData={{mktgId: 'legal-documents-type-select', dataType: 'legal-document-type'}}
            />
          </div>
        </div>

        <div className="flex w-full flex-col divide-y-[1px] divide-moss-grey-150 border-y border-moss-grey-150">
          {isLoading && (
            <div className="flex items-center justify-center py-16">
              <div className="text-tg-body-l text-moss-grey-550">{TRANSLATIONS.LOADING_DOCUMENTS[currentLocale]}</div>
            </div>
          )}

          {error && (
            <div className="flex items-center justify-center py-16">
              <div className="text-tg-body-l text-moss-grey-550">{TRANSLATIONS.NO_DOCUMENTS_FOUND[currentLocale]}</div>
            </div>
          )}

          {hasNoDocuments && (
            <div className="flex items-center justify-center py-16">
              <div className="text-tg-body-l text-moss-grey-550">{TRANSLATIONS.NO_DOCUMENTS_FOUND[currentLocale]}</div>
            </div>
          )}

          {hasDocuments && (
            <>
              {data.map((document, index) => (
                <DocumentRow key={`${document.id}-${index}`} document={document} locale={currentLocale} />
              ))}
            </>
          )}
        </div>

        <div className="mt-12 flex flex-col gap-4">
          <h2 id="partners" className="text-24 font-medium leading-[30px] text-moss-black">
            {slice.primary.legaltitle}
          </h2>
          <PrismicRichText
            components={{
              heading6: ({children}) => <h6 className="text-14 leading-[20px] text-moss-grey-500">{children}</h6>,
              paragraph: ({children}) => <p className="text-14 leading-[20px] text-moss-grey-500">{children}</p>,
              list: ({children}) => (
                <ul className="list-disc pl-5 text-14 leading-[20px] text-moss-grey-500">{children}</ul>
              ),
              listItem: ({children}) => <li className="mb-1">{children}</li>,
              strong: ({children}) => <strong className="font-medium">{children}</strong>,
            }}
            field={slice.primary.legalsubtitle}
          />
        </div>
      </div>
    </Container>
  )
}

export default LegalDocuments
